import { useEffect, useState } from 'react';

import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { Tooltip } from '@bringg/react-components';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { useTranslation } from 'react-i18next';

import {
	DeletedPolygonPoint,
	listenToPathChanges,
	restorePolygonPoint
} from 'bringg-web/features/service-area/utils/delete-polygon-point/delete-polygon-point';
import { createVertexAnchor } from 'bringg-web/features/service-area/utils/vertex-anchor/vertex-anchor';

import styles from './polygon-point-undo.module.scss';

// the sprite Google Maps draws its own undo arrow from (normal, hover, pressed); undocumented, so it has a fallback
const UNDO_SPRITE_URL = 'https://maps.gstatic.com/mapfiles/undo_poly.png';

interface Props {
	map: google.maps.Map;
	deleted: DeletedPolygonPoint;
	onClose: () => void;
}

export function PolygonPointUndo({ map, deleted, onClose }: Props) {
	const { t } = useTranslation();
	const [anchor, setAnchor] = useState<HTMLDivElement>(null);
	const [isSpriteMissing, setIsSpriteMissing] = useState(false);

	useEffect(() => {
		const sprite = new Image();

		sprite.onerror = () => setIsSpriteMissing(true);
		sprite.src = UNDO_SPRITE_URL;

		return () => {
			sprite.onerror = null;
		};
	}, []);

	useEffect(() => {
		const removeAnchor = createVertexAnchor(map, deleted.position, setAnchor);
		// like Google's own undo arrow, it only undoes the latest edit
		const stopListening = listenToPathChanges(deleted.polygon, onClose);

		return () => {
			stopListening();
			removeAnchor();
			setAnchor(null);
		};
	}, [map, deleted, onClose]);

	if (!anchor) {
		return null;
	}

	const label = t('SERVICE_AREA.UNDO_DELETE_POLYGON_POINT');

	return createPortal(
		<Tooltip title={label} placement="top">
			<button
				type="button"
				className={classNames(styles.polygonPointUndo, { [styles.fallback]: isSpriteMissing })}
				style={isSpriteMissing ? undefined : { backgroundImage: `url(${UNDO_SPRITE_URL})` }}
				aria-label={label}
				data-test-id="undo-delete-polygon-point"
				onClick={() => restorePolygonPoint(deleted)}
			>
				{isSpriteMissing && <BringgIcon iconName={BringgFontIcons.Undo} />}
			</button>
		</Tooltip>,
		anchor
	);
}
